import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { HarvestPlanService } from '../proxy/operational/controllers';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  HarvestPlanCreateDto,
  HarvestPlanUpdateDto,
} from '../proxy/operational/dtos/harvest-plan';
import { ACTION_IDS, HARVESTPLAN_ACTIONS, HARVESTPLAN_CONFIG, HARVESTPLAN_FIELDS } from './harvest-plan.config';
import { BaseCrudComponent, createImportExcelModalConfig, CrudPage, ExcelImportErrorsExportLike, ExcelImportResultLike, GenericForm, IActionsSchema, ICrudService, IMPORT_AGAIN_LABEL, IMPORT_MAX_SIZE_MB, IMPORT_MODAL_TITLE, ModalBuilder, PlanTypes, resolveActions, ZnExcelImportErrorsModal, ZnFileUploader } from '@zendra/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlanCreationService } from '../services/plan-creation.service';
import { HarvestPlanPlanningOrchestratorService } from 'orchestrator';
import {
  HarvestPlanProjectionDto,
  HarvestPlanProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/harvest-plans/models';

const IMPORT_TEMPLATE_FILE_NAME = 'HarvestPlanImportTemplate.xlsx';

type HarvestPlanImportResult = ExcelImportResultLike<unknown> & {
  fileName?: string;
};

@Component({
  selector: 'app-harvest-plan',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [ListService, HarvestPlanService, ConfirmationService, PlanCreationService],
  templateUrl: './harvest-plan.html',
  styleUrl: './harvest-plan.scss',
})
export class HarvestPlan extends BaseCrudComponent<
  HarvestPlanProjectionDto,
  HarvestPlanCreateDto,
  HarvestPlanUpdateDto,
  HarvestPlanProjectionExcelDownloadDto
> implements OnInit {
  private readonly harvestPlanService = inject(HarvestPlanService);
  private readonly planCreationService = inject(PlanCreationService);

  private readonly orchestratorService = inject(HarvestPlanPlanningOrchestratorService);
  private readonly modalService = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);

  service: ICrudService<
    HarvestPlanProjectionDto,
    HarvestPlanCreateDto,
    HarvestPlanUpdateDto,
    HarvestPlanProjectionExcelDownloadDto
  > = {
    create: input => this.harvestPlanService.create(input) as any,
    update: (id, input) => this.harvestPlanService.update(id, input) as any,
    deleteByIds: ids => this.harvestPlanService.deleteByIds(ids),
    getList: input => this.orchestratorService.getList(input),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'harvestPlanId',
    ),
  };

  config = HARVESTPLAN_CONFIG;
  fields = HARVESTPLAN_FIELDS;
  actions: IActionsSchema | null = null;

  private readonly triggerMap: Record<number, () => void> = {
    [ACTION_IDS.importExcelModal]: () => this.openImportModal(),
    [ACTION_IDS.downloadExcelTemplateTrigger]: () =>
      this.downloadExcelTemplate(),
  };

  ngOnInit(): void {
    this.actions = resolveActions(
      HARVESTPLAN_ACTIONS,
      this.triggerMap,
    );
  }

  override create(): void {
    this.planCreationService.openCreate({
      planType: PlanTypes.Harvest,
      fields: this.fields,
      form: this.genericForm,
      injector: this.injector,
      destroyRef: this.destroyRef,
      title: `${this.localizationResource.LanguageService}::TableNewRegistry`,
      entityLocaleKey: this.config.localizationKey,
      onResolved: values => {
        this.selectedEntity = values;
      },
    });
  }

  protected prepareUpdateDto(data: any, selected: HarvestPlanProjectionDto): HarvestPlanUpdateDto {
    return {
      team: data.team,
      year: data.year,
      validDate: data.validDate,
      volume: data.volume,
      planVersion: data.planVersion,
      planNumber: data.planNumber,
      costReceiverId: data.costReceiverId,
      planTypeId: selected.planTypeId,
      versionId: data.versionId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'HarvestPlan';
  }

  private openImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.HarvestPlan',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importHarvestPlan(formData),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: HarvestPlanImportResult) => {
        if (result?.hasErrors) {
          this.openImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openImportErrorsModal(
    result: HarvestPlanImportResult,
  ): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.HarvestPlan',
        confirmLabel: IMPORT_AGAIN_LABEL,
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: IMPORT_MODAL_TITLE,
          subTitle: 'Operational::Entity.HarvestPlan',
          exportErrors: (
            input: ExcelImportErrorsExportLike,
          ) =>
            this.orchestratorService.exportHarvestPlanImportErrors(
              input,
            ),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openImportModal();
        }
      });
  }

  private downloadExcelTemplate(): void {
    this.orchestratorService
      .getHarvestPlanImportTemplate()
      .subscribe({
        next: result => {
          this.toaster.success(
            'LanguageService::Actions:DownloadTemplateMessage',
            null,
            {
              life: 5000,
              messageLocalizationParams: [
                this.localizationService.instant(
                  'LanguageService::Actions:ExcelTemplateDownload',
                ),
              ],
            },
          );
          this.downloadFile(
            result,
            IMPORT_TEMPLATE_FILE_NAME,
          );
        },
        error: error => {
          this.toaster.error(error, null, {
            life: 5000,
          });
        },
      });
  }
}
